"use client";

import { useSettingEnableBrandHoverCard } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableBrandHoverCard";
import { Core } from "@faire/slate/foundation/color";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { Column, Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { Popover } from "@faire/web/slate/utils/Popover/Popover";
import { PopoverContent } from "@faire/web/slate/utils/Popover/PopoverContent";
import { NoTranslate } from "@faire/web/ui/lib/localization";
import React, { useState } from "react";

// Extract total product count from catalogSizeText (e.g., "55 products" -> 55)
const getTotalProductCount = (catalogSizeText?: string): number => {
  if (!catalogSizeText) {
    return 0;
  }
  const match = catalogSizeText.match(/(\d+)\s*products?/i);
  return match && match[1] ? parseInt(match[1], 10) : 0;
};

export interface BrandHoverCardData {
  catalogSizeText?: string;
  images: Array<IImage>;
  bestImage?: IImage;
}

interface BrandHoverCardProps {
  children: React.ReactNode;
  brandHoverData?: BrandHoverCardData;
}

export const BrandHoverCard: React.FC<BrandHoverCardProps> = ({
  children,
  brandHoverData,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isBrandHoverCardEnabled = useSettingEnableBrandHoverCard();

  // Return children if setting is not enabled or no brand hover data
  if (!isBrandHoverCardEnabled || !brandHoverData) {
    return <>{children}</>;
  }

  const totalProductCount = getTotalProductCount(
    brandHoverData.catalogSizeText
  );
  const maxDisplayImages = 3;
  const productImages = brandHoverData.images.slice(0, maxDisplayImages);
  const fallbackImage = brandHoverData.bestImage;

  // Calculate remaining count for "x more products" text
  const remainingCount = Math.max(0, totalProductCount - maxDisplayImages);

  return (
    <Popover
      open={isOpen}
      placement="bottom-start"
      onClickOutside={() => setIsOpen(false)}
      width={244}
      zIndex={1000}
    >
      <div
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        style={{ display: "inline-block" }}
      >
        {children}
      </div>
      <PopoverContent>
        <div
          style={{
            padding: "12px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e5e7eb",
          }}
        >
          <Column gap={8}>
            {/* Product Images - Single Row */}
            {(productImages.length > 0 || fallbackImage) && (
              <Row gap={2}>
                {productImages.length > 0
                  ? productImages.map((image, index) => (
                      <ProductImageThumbnail key={index} image={image} />
                    ))
                  : fallbackImage && (
                      <ProductImageThumbnail image={fallbackImage} />
                    )}
              </Row>
            )}

            {/* More products text - Second Row */}
            {remainingCount > 0 && (
              <NoTranslate>
                <Typography
                  variant="labelSansRegular"
                  color={Core.text.subdued}
                  style={{ textAlign: "left" }}
                >
                  {remainingCount} more products
                </Typography>
              </NoTranslate>
            )}
          </Column>
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface ProductImageThumbnailProps {
  image: IImage;
}

const ProductImageThumbnail: React.FC<ProductImageThumbnailProps> = ({
  image,
}) => {
  return (
    <div
      style={{
        width: "72px",
        height: "72px",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src={image.url}
        alt={image.alt_text || "Product image"}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(e) => {
          // Handle image load error
          e.currentTarget.style.display = "none";
        }}
      />
    </div>
  );
};
